import { Box, Container, Typography, List, ListItem, ListItemText, ListItemIcon, Button, useTheme, useMediaQuery, Drawer, IconButton } from '@mui/material';
import { Settings as SettingsIcon, AccountCircle, Schedule, LocationOn, ArrowBack, Menu as MenuIcon, CalendarMonth, Payment, Key } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import Layout from '../Layout';

const ProfileLayout = ({ children, activeKey }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);

  const navigationContent = (
    <>
      <Box sx={{ mb: 3 }}>
        <Button
          component={Link}
          to="/channels"
          startIcon={<ArrowBack />}
          sx={{
            color: 'text.primary',
            textTransform: 'none',
            fontWeight: 'medium',
            p: 0,
            '&:hover': {
              backgroundColor: 'transparent',
              color: 'primary.main',
            }
          }}
        >
          Go Back
        </Button>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <SettingsIcon sx={{ mr: 1, color: 'text.primary' }} />
        <Typography variant="h5" color="text.primary">
          Settings
        </Typography>
      </Box>

      <List component="nav" sx={{
        '& .MuiListItem-root': {
          borderRadius: 1,
          mb: 1,
          transition: 'all 0.2s ease',
          '&:hover': {
            bgcolor: 'action.hover',
            transform: 'translateX(4px)',
          },
          '&.active': {
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            boxShadow: theme.shadows[2],
            '& .MuiListItemIcon-root': {
              color: 'inherit',
            },
            '& .MuiTypography-root': {
              color: 'inherit',
            },
          },
        }
      }}>
        <ListItem
          button
          component={Link}
          to="/profile"
          className={activeKey === 'account' ? 'active' : ''}
          onClick={() => isMobile && setDrawerOpen(false)}
        >
          <ListItemIcon>
            <AccountCircle color="inherit" />
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography variant="subtitle1" fontWeight={500}>
                Account
              </Typography>
            }
            secondary={
              <Typography variant="caption" color={activeKey === 'account' ? 'inherit' : 'text.secondary'}>
                Email, password and more
              </Typography>
            }
          />
        </ListItem>

        <ListItem
          button
          component={Link}
          to="/booking-availability"
          className={activeKey === 'booking' ? 'active' : ''}
          onClick={() => isMobile && setDrawerOpen(false)}
        >
          <ListItemIcon>
            <Schedule color="inherit" />
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography variant="subtitle1" fontWeight={500}>
                Your Availability
              </Typography>
            }
            secondary={
              <Typography variant="caption" color={activeKey === 'booking' ? 'inherit' : 'text.secondary'}>
                Office/Working Hours
              </Typography>
            }
          />
        </ListItem>

        <ListItem
          button
          component={Link}
          to="/travel-and-location"
          className={activeKey === 'travel' ? 'active' : ''}
          onClick={() => isMobile && setDrawerOpen(false)}
        >
          <ListItemIcon>
            <LocationOn color="inherit" />
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography variant="subtitle1" fontWeight={500}>
                Advanced Scheduling
              </Typography>
            }
            secondary={
              <Typography variant="caption" color={activeKey === 'travel' ? 'inherit' : 'text.secondary'}>
                Buffers, drive times and filters
              </Typography>
            }
          />
        </ListItem>

        <ListItem
          button
          component={Link}
          to="/google-calendar"
          className={activeKey === 'google_calendar' ? 'active' : ''}
          onClick={() => isMobile && setDrawerOpen(false)}
        >
          <ListItemIcon>
            <CalendarMonth color="inherit" />
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography variant="subtitle1" fontWeight={500}>
                Google Calendar
              </Typography>
            }
            secondary={
              <Typography variant="caption" color={activeKey === 'google_calendar' ? 'inherit' : 'text.secondary'}>
                Calendar integration settings
              </Typography>
            }
          />
        </ListItem>

        <ListItem
          button
          component={Link}
          to="/api-keys"
          className={activeKey === 'api_keys' ? 'active' : ''}
          onClick={() => isMobile && setDrawerOpen(false)}
        >
          <ListItemIcon>
            <Key color="inherit" />
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography variant="subtitle1" fontWeight={500}>
                API Keys
              </Typography>
            }
            secondary={
              <Typography variant="caption" color={activeKey === 'api_keys' ? 'inherit' : 'text.secondary'}>
                Manage your API credentials
              </Typography>
            }
          />
        </ListItem>

        <ListItem
          button
          component={Link}
          to="/billing-and-plans"
          className={activeKey === 'billing_and_plans' ? 'active' : ''}
          onClick={() => isMobile && setDrawerOpen(false)}
        >
          <ListItemIcon>
            <Payment color="inherit" />
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography variant="subtitle1" fontWeight={500}>
                Billing & Plans
              </Typography>
            }
            secondary={
              <Typography variant="caption" color={activeKey === 'billing_and_plans' ? 'inherit' : 'text.secondary'}>
                Manage your subscription and billing
              </Typography>
            }
          />
        </ListItem>
      </List>
    </>
  );

  return (
    <Layout>
      <Container maxWidth={false} sx={{ py: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', minHeight: 'calc(100vh - 120px)' }}>
          {/* Mobile Menu Button */}
          {isMobile && (
            <Box sx={{ position: 'fixed', top: 70, left: 16, zIndex: 1200 }}>
              <IconButton
                color="primary"
                onClick={() => setDrawerOpen(true)}
                sx={{
                  bgcolor: 'background.paper',
                  boxShadow: theme.shadows[2],
                  '&:hover': {
                    bgcolor: 'background.paper',
                    boxShadow: theme.shadows[4],
                  }
                }}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          )}

          {/* Desktop Sidebar */}
          {!isMobile && (
            <Box
              sx={{
                width: 280,
                flexShrink: 0,
                bgcolor: 'background.paper',
                borderRadius: 2,
                p: 3,
                mr: 3,
                height: 'fit-content',
                position: 'sticky',
                top: 16,
                boxShadow: theme.shadows[1],
              }}
            >
              {navigationContent}
            </Box>
          )}

          {/* Mobile Drawer */}
          <Drawer
            anchor="left"
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            sx={{
              '& .MuiDrawer-paper': {
                width: 280,
                bgcolor: 'background.paper',
                p: 3,
              }
            }}
          >
            {navigationContent}
          </Drawer>

          {/* Main Content */}
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              bgcolor: 'background.default',
              borderRadius: 2,
              overflow: 'auto',
              ml: isMobile ? 0 : 0,
              width: '100%',
            }}
          >
            <Box
              sx={{
                p: { xs: 2, sm: 3, md: 4 },
                maxWidth: { xs: '100%', lg: 1200 },
                mx: 'auto',
              }}
            >
              {children}
            </Box>
          </Box>
        </Box>
      </Container>
    </Layout>
  );
};

export default ProfileLayout;
